<script setup>
import { computed, ref, watch } from 'vue';
import store from '@/store';
import util from '@/util';
import service from '@/service';
import i18n from '@/i18n';
import { AGREEMENT, CONTEXT, MOBILE_WIDTH } from '@/const';
import Agreement from '@/component/userAgreements/AgreementService';

const enableHuaweiIdLogin = ref(false);
const enableThirdLogin = ref(false);

const huaweiIdUrl = ref('');
const huaweiIdParams = ref({});
const huaweiIdAction = () =>
  util.jumpTo(`/uniportal1${huaweiIdUrl.value}`, {
    ...huaweiIdParams.value,
    agreement: Agreement.hasAgree ? AGREEMENT.yes : AGREEMENT.no,
  });

const huaweiButton = {
  img: require('@/asset/img/Huawei.svg'),
  tip: i18n.t('tooltip.Huawei'),
  id: 'Huawei',
};

const defaultButtons = [
  huaweiButton,
  { img: require('@/asset/img/QQ.svg'), tip: i18n.t('tooltip.Tencent'), id: 'Tencent' },
  { img: require('@/asset/img/Wechat.svg'), tip: i18n.t('tooltip.Wechat'), id: 'Wechat' },
  { img: require('@/asset/img/Weibo.svg'), tip: i18n.t('tooltip.Weibo'), id: 'Weibo' },
  { img: require('@/asset/img/Google.svg'), tip: i18n.t('tooltip.Google'), id: 'Google' },
  { img: require('@/asset/img/Facebook.svg'), tip: i18n.t('tooltip.Facebook'), id: 'Facebook' },
  { img: require('@/asset/img/Linkedin.svg'), tip: i18n.t('tooltip.Linkedin'), id: 'Linkedin' },
];
const defaultId = defaultButtons.map(e => e.id);

const configButtonId = ref('');

const nonDefaultButtons = ref([]);

const redirect = util.getURLSearchParam('redirect') || '';
const wap = util.getURLSearchParam('wap');
const idaasExtField = util.getURLSearchParam('idaas_ext_field');

const isWechat = () => /MicroMessenger/i.test(navigator.userAgent).toString();

const toAuthSourceLogin = ({ sourceCode, sourceType }) => {
  util.jumpTo(`${CONTEXT.uniportal}/rest/hwidcenter/auth-source-login`, {
    sourceCode,
    sourceType,
    redirect,
  });
};

const toOauth2 = id => {
  const params = {
    redirect,
    client: 'mobile',
    wechat: isWechat(),
    agreement: Agreement.hasAgree ? AGREEMENT.yes : AGREEMENT.no,
  };

  if (wap) {
    params.wap = wap;
  }
  if (idaasExtField) {
    params['idaas_ext_field'] = idaasExtField;
  }
  util.jumpTo(`${CONTEXT.uniportal}/oauth2/${id.toLowerCase()}`, params);
};

const toThirdLogin = ({ id, sourceCode, sourceType }) => {
  if (id === 'Huawei') {
    huaweiIdAction();
    return;
  }
  if (nonDefaultButtons.value.map(e => e.id).includes(id)) {
    toAuthSourceLogin({ sourceCode, sourceType });
    return;
  }
  if (window.innerWidth <= MOBILE_WIDTH) {
    toOauth2(id);
    return;
  }
  const params = {
    wechat: isWechat(),
    redirect,
  };
  if (wap) {
    params.wap = wap;
  }
  if (idaasExtField) {
    params['idaas_ext_field'] = idaasExtField;
  }
  const url = util.assembleURL(`${CONTEXT.uniportal}/oauth2/${id.toLowerCase()}`, params);
  const width = 760;
  const height = 600;
  const left = Math.floor((screen.availWidth - width) / 2);
  const top = Math.floor((screen.availHeight - height) / 2);
  const position = Object.entries({ left, top, width, height })
    .map(e => e.join('=') + 'px')
    .join(',');
  window.open(url, id, position);
};

const handleClick = item => {
  Agreement.open({
    countryCode: '',
    confirm: () => toThirdLogin(item),
  });
};

const setAuthSourceConfig = async thirdpartyType => {
  const nonDefaultSourceCodes = configButtonId.value
    .split(',')
    .filter(e => e && !defaultId.includes(e));
  const res = await service.getAuthSourceConfig(nonDefaultSourceCodes);
  const medium = res?.medium;
  nonDefaultButtons.value = medium.map(e => ({
    img: e.logo,
    tip: i18n.locale === 'zh_CN' ? e.sourceNameCn : e.sourceNameEn,
    id: e.id,
    sourceCode: e.sourceCode,
    sourceType: e.sourceType,
  }));
  if (thirdpartyType !== 1 || !configButtonId.value) {
    return;
  }
  // 直接跳转
  const redirectName = configButtonId.value.toLowerCase();
  if (redirectName === 'huawei') {
    huaweiIdAction();
    return;
  }
  const button = nonDefaultButtons.value.find(e => e?.sourceCode === redirectName);
  if (button) {
    toAuthSourceLogin(button);
  } else {
    toOauth2(redirectName);
  }
};

const appPolicy = computed(() => store.state.appPolicy);
watch(appPolicy, appPolicy => {
  const { enablehwidLogin, hwidThirdMibilePolicy, hwLoginAddress, hwLoginAddressMobile } =
    appPolicy;
  // 华为ID登录
  if (enablehwidLogin === 1) {
    enableHuaweiIdLogin.value = true;
    if (hwidThirdMibilePolicy === 'oauth2') {
      huaweiIdUrl.value = hwLoginAddress;
      huaweiIdParams.value = { wap: 1 };
    } else {
      huaweiIdUrl.value = hwLoginAddressMobile;
    }
  }
  // 其他三方登录
  const { thirdPartyAllow, status } = appPolicy;
  if (thirdPartyAllow && status === 1) {
    enableThirdLogin.value = true;
  }
  const { thirdpartyType, hideSection } = appPolicy;
  configButtonId.value = hideSection;
  setAuthSourceConfig(thirdpartyType);
  if (thirdpartyType === 1 && hideSection) {
    if (hideSection === 'Huawei') {
      huaweiIdAction();
    } else {
      toOauth2(hideSection);
    }
  }
});

const buttons = computed(() => {
  if (enableHuaweiIdLogin.value && !enableThirdLogin.value) {
    return [huaweiButton];
  }
  if (enableThirdLogin.value) {
    const configDefaultButtons = defaultButtons.filter(e => configButtonId.value.includes(e.id));
    return [...configDefaultButtons, ...nonDefaultButtons.value];
  }
  return [];
});
</script>

<template>
  <div class="h-20 pt-3">
    <div class="line h-3 flex justify-center relative" v-if="buttons.length > 0">
      <div class="line-text absolute text-xs leading-6 px-4">{{ $t('layout.line') }}</div>
    </div>
    <div
      class="pt-2 pb-6 pl-4 h-17 overflow-x-scroll whitespace-nowrap flex other-login-container"
      :class="{ 'justify-center': buttons.length <= 6 }"
      v-if="buttons.length > 0"
    >
      <div v-for="item in buttons" :key="item.id">
        <el-tooltip effect="dark" :content="item.tip" placement="top">
          <div
            class="w-9 h-9 cursor-pointer flex-shrink-0 overflow-hidden"
            :class="{ 'mr-4': buttons.length <= 6, 'custom-img': buttons.length > 6 }"
            @click="handleClick(item)"
          >
            <img class="w-9 h-9" :src="item.img" alt="" />
          </div>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
.line {
  border-top: 1px solid #f3f3f3;
}

.line-text {
  color: #aeaeae;
  background-color: #ffffff;
  top: -12px;
}

.img {
  transform: scale(1.5);
}

.custom-img {
  margin-right: 18px;
}

.other-login-container::-webkit-scrollbar {
  width: 0;
  height: 0;
}
</style>
